<template>
    <div @mouseover="handleRootMouseOver" @mouseout="handleRootMouseOut">
        <!-- Processed text with citations -->
        <div class="citation-text" v-html="processedText"></div>

        <!-- References -->
        <div
            class="citation-references-container"
            v-for="(file, index) in uniqueFileHashes"
            :key="index"
        >
            <div class="citation-reference">
                <div class="citation-reference-index">[{{ index + 1 }}]</div>
                <div class="citation-info">
                    <div
                        class="citation-link"
                        @click="openPdf(fileDetails[file])"
                    >
                        {{ fileDetails[file]?.original_filename }}
                    </div>
                    <div
                        v-if="
                            fileDetails[file]?.page_number !== undefined &&
                            fileDetails[file]?.page_number !== null
                        "
                        class="citation-page"
                    >
                        Page {{ fileDetails[file].page_number }}
                        <span
                            v-if="
                                fileDetails[file]?.total_pages !== undefined &&
                                fileDetails[file]?.total_pages !== null
                            "
                        >
                            of {{ fileDetails[file].total_pages }}
                        </span>
                    </div>
                </div>
                <i class="fi fi-chevron-right"></i>
            </div>
        </div>
    </div>
</template>

<script>
import { marked } from 'marked';

export default {
    name: 'CitationText',
    props: {
        messageText: {
            type: String,
            required: true,
        },
        citations: {
            type: Array,
            default: () => [],
        },
    },
    emits: ['open-pdf'],
    data() {
        return {
            // fileDetails: {},
            tooltip: null,
            tooltipContent: null,
            hideTooltipTimeout: null,
            tooltipsInitialized: false,
        };
    },
    computed: {
        processedText() {
            const text = this.removeSourceReference(this.messageText);
            if (!this.citations?.length) {
                return this.parseMarkdown(text);
            }
            return this.insertCitationsAndParseMarkdown(text);
        },

        // Get the unique files
        uniqueFileHashes() {
            if (!this.citations?.length) {
                return [];
            }

            return [
                ...new Set(
                    this.citations
                        .map((citation) => citation?.file_hash)
                        .filter(Boolean)
                ),
            ];
        },

        fileDetails() {
            const files = {};
            this.uniqueFileHashes.forEach((hash) => {
                const file = this.citations.find(
                    (citation) => citation?.file_hash === hash
                );
                // add the hash to the file object to be used in pdf viewer as hash itself instead of file_hash
                files[hash] = { ...file, hash: hash };
            });
            return files;
        },
    },
    watch: {
        citations() {
            if (this.tooltip) {
                this.hideTooltip();
            }
        },
        messageText() {
            if (this.tooltip) {
                this.hideTooltip();
            }
        },
    },
    methods: {
        // --- Text Processing & Citation Logic ---
        parseMarkdown(text) {
            return marked.parse(text ?? ''); // Ensure text is not null/undefined
        },

        insertCitationsAndParseMarkdown(text) {
            let filteredCitations = [];

            for (const citation of [...this.citations]) {
                const match = filteredCitations.find((c) => {
                    return (
                        c.index == citation.index &&
                        c.file_hash == citation.file_hash
                    );
                });
                if (!match) filteredCitations.push(citation);
            }

            const sortedCitations = filteredCitations.sort(
                (a, b) => (b.index ?? 0) - (a.index ?? 0)
            );

            const result = this.createTextWithCitationMarkers(
                sortedCitations,
                text
            );
            if (!result) {
                return this.parseMarkdown(text);
            }
            const { processedText, citationMarkers } = result;

            let parsedHTML = this.parseMarkdown(processedText);

            if (citationMarkers) {
                Object.entries(citationMarkers).forEach(([key, value]) => {
                    parsedHTML = parsedHTML.replaceAll(key, value);
                });
            }

            return parsedHTML;
        },

        createTextWithCitationMarkers(sortedCitations, originalText) {
            let processedText = originalText ?? '';
            const citationMarkers = {};

            for (let i = sortedCitations.length - 1; i >= 0; i--) {
                const citation = sortedCitations[i];

                const fileHash = citation?.file_hash;
                if (!fileHash) continue; // Skip citation if no file_hash

                const citationIndex =
                    this.uniqueFileHashes.indexOf(fileHash) + 1;
                // Ensure citation.index is a number, default to 0 if not
                const markerKey = `[[CITATION_${citationIndex}:${
                    citation.index ?? 0
                }]]`;
                const markerHtml = this.createCitationMarkerHTML(
                    citation.index ?? 0,
                    fileHash,
                    citationIndex
                );

                citationMarkers[markerKey] = markerHtml;

                const index = citation.index ?? 0;
                processedText =
                    processedText.slice(0, index) +
                    markerKey +
                    processedText.slice(index);
            }

            return { processedText, citationMarkers };
        },

        createCitationMarkerHTML(position, fileHash, citationIndex) {
            return `<span class="citation-wrapper" data-citation-id="${position}:${fileHash}" data-citation-index="${citationIndex}">
                <sup><span class="citation-marker">[${citationIndex}]</span></sup>
            </span>`;
        },

        // --- UI Interaction & Event Emitting ---
        openPdf(file) {
            if (file) {
                // Ensure file object exists
                this.$emit('open-pdf', file);
            }
        },

        // --- Tooltip Management ---
        initializeTooltips() {
            if (this.tooltipsInitialized) return; // Already initialized

            this.createTooltip(); // Create tooltip DOM element if not already present

            this.tooltipsInitialized = true;
        },

        createTooltip() {
            if (this.tooltip) return; // Tooltip already exists

            const tooltipElement = document.createElement('div');
            tooltipElement.className = 'citation-tooltip';

            const tooltipContentElement = document.createElement('div');
            tooltipContentElement.className = 'tooltip-content';
            tooltipElement.appendChild(tooltipContentElement);

            document.body.appendChild(tooltipElement);

            this.tooltip = tooltipElement;
            this.tooltipContent = tooltipContentElement;

            this.tooltip.addEventListener(
                'mouseenter',
                this.clearHideTooltipTimeout
            );
            this.tooltip.addEventListener(
                'mouseleave',
                this.startHideTooltipTimeout
            );
        },

        removeTooltip() {
            if (!this.tooltip?.parentNode) return; // No tooltip or not in DOM

            this.tooltip.removeEventListener(
                'mouseenter',
                this.clearHideTooltipTimeout
            );
            this.tooltip.removeEventListener(
                'mouseleave',
                this.startHideTooltipTimeout
            );
            this.tooltip.parentNode.removeChild(this.tooltip);

            this.tooltip = null;
            this.tooltipContent = null;
            this.clearHideTooltipTimeout(); // Clear any pending timeout
        },

        showTooltip(marker, fileHash) {
            if (!this.tooltip || !this.tooltipContent) {
                // Ensure tooltip elements exist
                this.createTooltip(); // Attempt to create if missing
                if (!this.tooltip) return; // If still missing, exit
            }

            this.clearHideTooltipTimeout();

            const title =
                this.fileDetails[fileHash]?.original_filename ??
                'Unknown document';
            const pageNumber = this.fileDetails[fileHash]?.page_number;
            const totalPages = this.fileDetails[fileHash]?.total_pages;

            const rect = marker.getBoundingClientRect();

            this.tooltip.style.left = `${rect.left + rect.width / 2}px`;
            this.tooltip.style.top = `${rect.top - 10}px`; // Adjust as needed

            let tooltipHTML = `<div class="text-link tooltip-title">${title}</div>`;

            // Add page information if available
            if (pageNumber !== undefined && pageNumber !== null) {
                if (totalPages !== undefined && totalPages !== null) {
                    tooltipHTML += `<div class="tooltip-page-info">Page ${pageNumber} of ${totalPages}</div>`;
                } else {
                    tooltipHTML += `<div class="tooltip-page-info">Page ${pageNumber}</div>`;
                }
            }

            this.tooltipContent.innerHTML = tooltipHTML;

            const fileElement =
                this.tooltipContent.querySelector('.tooltip-title');
            if (fileElement && this.fileDetails[fileHash]) {
                fileElement.onclick = () =>
                    this.openPdf(this.fileDetails[fileHash]);
            }

            this.tooltip.style.visibility = 'visible';
            this.tooltip.style.opacity = '1';
        },

        hideTooltip() {
            if (!this.tooltip) return;
            this.tooltip.style.visibility = 'hidden';
            this.tooltip.style.opacity = '0';
        },

        startHideTooltipTimeout() {
            this.clearHideTooltipTimeout(); // clear existing timeout
            this.hideTooltipTimeout = setTimeout(this.hideTooltip, 300);
        },

        clearHideTooltipTimeout() {
            if (this.hideTooltipTimeout) {
                clearTimeout(this.hideTooltipTimeout);
                this.hideTooltipTimeout = null;
            }
        },

        removeSourceReference(text) {
            // Remove source references using regex patterns
            // Pattern 1: 【4:0–4:4†source】
            // Pattern 2: 【16:【0†1748548718975-195085708.pdf】, 17: 0†1748548718975-195085708.pdf】】
            const pattern1 = /【.*†source】/g;
            const pattern2 = /【.*:【.*】】/g;

            return text.replace(pattern1, '').replace(pattern2, '');
        },

        // Event handlers for root div element
        handleRootMouseOver(event) {
            const marker = event.target.closest('.citation-marker');
            if (!marker) return;

            const wrapper = marker.closest('.citation-wrapper');
            const citationId = wrapper?.getAttribute('data-citation-id');
            if (!citationId) return;

            const [positionStr, fileHash] = citationId.split(':');
            const position = parseInt(positionStr);

            const citation = this.citations.find(
                (c) => c.file_hash === fileHash && c.index === position
            );

            if (citation) {
                this.showTooltip(marker, fileHash);
            }
        },
        handleRootMouseOut(event) {
            if (event.target.closest('.citation-marker')) {
                this.startHideTooltipTimeout();
            }
        },
    },
    mounted() {
        marked.setOptions({
            breaks: false,
            gfm: true,
            headerIds: false,
            xhtml: false,
        });
        // Then initialize tooltips if citations exist
        if (this.citations?.length > 0) {
            this.initializeTooltips();
        }
    },
    beforeUnmount() {
        this.removeTooltip();
        // Clear timeout an extra time to be safe
        this.clearHideTooltipTimeout();
    },
};
</script>

<style scoped>
.citation-text {
    font-size: var(--size-4);
    line-height: 1.625;
    font-weight: 400;
    color: var(--neutral-900);
    position: relative;
    min-width: 0;
    overflow-wrap: anywhere;
    word-break: break-word;
}

.citation-references-container {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    padding: var(--space-2);
    border-radius: var(--radius-default);
    background-color: var(--white);
    align-items: center;
    margin-top: var(--space-4);
}

.citation-reference {
    display: flex;
    align-items: center;
    gap: var(--space-1);
    padding-block: var(--space-1);
    padding-inline: var(--space-4);
    font-size: 0.9375rem;
    font-weight: 600;
    color: var(--primary-400);
    line-height: 1.2;
}

.citation-info {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
    flex: 1;
    min-width: 0;
}

.citation-link {
    text-decoration: underline;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    cursor: pointer;
}

.citation-page {
    font-size: 0.75rem;
    font-weight: 600;
    line-height: 1.3;
}

.citation-text :deep(.citation-wrapper) {
    display: inline-block;
    position: relative;
}

.citation-text :deep(.citation-marker) {
    display: inline-block;
    white-space: nowrap;
    font-weight: 700;
    font-size: 1rem;
    line-height: 1.625;
    color: var(--success);
    position: relative;
    cursor: pointer;
}

/* Fix for markdown formatting */
.citation-text :deep(p) {
    display: inline;
    margin: 0;
}

/* Create proper paragraph breaks */
.citation-text :deep(p:not(:last-child)) {
    margin-bottom: 0.5rem;
    display: block;
}

.citation-text :deep(ul),
.citation-text :deep(ol) {
    padding-left: 1.5rem;
    margin-bottom: 0.5rem;
    display: block;
}

.citation-text :deep(li) {
    display: list-item;
    margin-bottom: 0.25rem;
}

.citation-text :deep(code) {
    background-color: var(--neutral-100);
    padding: 0.2rem 0.4rem;
    border-radius: var(--radius-sm);
    font-family: monospace;
}

.citation-text :deep(pre) {
    background-color: var(--neutral-100);
    padding: 1rem;
    border-radius: var(--radius-default);
    overflow-x: auto;
    margin-bottom: 0.5rem;
    display: block;
}

.citation-text :deep(a) {
    color: var(--primary-700);
    text-decoration: underline;
}

.citation-text :deep(strong) {
    font-weight: 700;
}
</style>

<style>
/* Global tooltip styles */
.citation-tooltip {
    visibility: hidden;
    position: fixed;
    background-color: var(--white);
    color: var(--neutral-900);
    padding: var(--space-2);
    border-radius: var(--radius-default);
    max-width: 12.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    z-index: 9999;
    opacity: 0;
    transition: opacity 0.2s, visibility 0.2s;
    transform: translate(-50%, -100%);
    pointer-events: auto;
}

.citation-tooltip::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    margin-left: -6px;
    border-width: 6px;
    border-style: solid;
    border-color: var(--white) transparent transparent transparent;
    pointer-events: none;
}

.tooltip-content {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.tooltip-title {
    font-weight: 600;
    font-size: 0.875rem;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: normal;
    cursor: pointer;
}

.tooltip-page-info {
    font-size: 0.75rem;
    font-weight: 600;
    margin-top: 0.25rem;
    color: var(--primary-400);
}
</style>
